import React, { useState, useRef, useEffect } from 'react';
import { pokemonData } from '../data/pokemon';

function SearchBar({ onSearch, placeholder }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Filter suggestions based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = pokemonData
      .filter(pokemon => 
        pokemon.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, 8); // Limit to 8 suggestions

    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
    setSelectedIndex(-1);
  }, [searchTerm]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else if (suggestions.length > 0) {
          // If no suggestion is selected but there are suggestions, select the first one
          handleSuggestionClick(suggestions[0]);
        } else {
          setShowSuggestions(false);
          setSelectedIndex(-1);
          onSearch(searchTerm);
          setSearchTerm(''); // Clear the search term after submission
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        inputRef.current?.blur();
        break;
      default:
        // Handle other keys (do nothing)
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (pokemon) => {
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSearch(pokemon.name);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSearch(searchTerm);
    setSearchTerm(''); // Clear the search term after submission
  };

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative flex-1" ref={suggestionsRef}>
      <form onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
          autoComplete="off"
        />
      </form>

      {/* Auto-complete suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-xl z-10 max-h-64 overflow-y-auto">
          {suggestions.map((pokemon, index) => (
                         <button
               key={pokemon.id}
               onClick={() => handleSuggestionClick(pokemon)}
               className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                 index === selectedIndex ? 'bg-blue-50' : ''
               } ${index === 0 ? 'rounded-t-lg' : ''} ${
                 index === suggestions.length - 1 ? 'rounded-b-lg' : ''
               }`}
             >
               <img
                 src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemon.id}.png`}
                 alt={pokemon.name}
                 className="w-8 h-8 bg-gray-100 rounded"
                 onError={(e) => {
                   e.target.style.display = 'none';
                 }}
               />
               <span className="text-gray-600 font-mono text-sm w-8">
                 #{pokemon.id.toString().padStart(3, '0')}
               </span>
               <span className="text-gray-800 font-medium">{pokemon.name}</span>
               <div className="ml-auto flex gap-1">
                 {pokemon.types.map((type) => (
                   <span
                     key={type}
                     className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700 font-medium"
                   >
                     {type}
                   </span>
                 ))}
               </div>
             </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
